import { inject, Service } from '@angular/core';
import type { OnyxCallbackOption, OnyxModalResult } from '@onyx/angular';
import {
  ActionHelper,
  OnyxModalService,
  OnyxToastService,
} from '@onyx/angular';
import type { Subject } from 'rxjs';
import { RouteHelper } from '../../../../../common/helpers/route.helper';
import { FleetIdentifierPipe } from '../../../../../common/pipes/fleet-identifier.pipe';
import type { SwapModalData } from '../../components/swap-modal/swap-modal.component';
import { SwapModalComponent } from '../../components/swap-modal/swap-modal.component';
import type { SwapStatusModalData } from '../../components/swap-status-modal/swap-status-modal.component';
import { SwapStatusModalComponent } from '../../components/swap-status-modal/swap-status-modal.component';
import { MapRoute } from '../../enums/map-route';
import type { Swap } from '../../interfaces/swap/swap';

@Service()
export class SwapModalHelper {
  private readonly modalService = inject(OnyxModalService);
  private readonly toastService = inject(OnyxToastService);
  private readonly routeHelper = inject(RouteHelper);
  private readonly fleetIdentifierPipe = inject(FleetIdentifierPipe);

  public getOptions(swap: Swap, close$?: Subject<void>): OnyxCallbackOption[] {
    const hasCompletedSwap = RouteHelper.hasCompletedSwap(swap.technicalPoints);

    return [
      {
        name: 'route.changeStatus',
        value: () =>
          this.modalService.open<SwapStatusModalData>(
            SwapStatusModalComponent,
            {
              swapUuid: swap.uuid,
              vehicleSet: {
                vehicle: swap.vehicle,
                trailer: swap.initialSet.trailer,
              },
              otherVehicleSet: swap.otherVehicle
                ? {
                    vehicle: swap.otherVehicle,
                    trailer: swap.otherInitialSet?.trailer ?? null,
                  }
                : null,
            },
          ),
        leftIcon: { name: 'edit-outlined', size: 20 },
        leftIconColor: 'gray',
      },
      {
        name: 'labels.revoke',
        value: () => this.routeHelper.cancelSwap(swap.uuid, close$),
        leftIcon: { name: 'clear-outlined', size: 20 },
        leftIconColor: 'red',
        disabled: hasCompletedSwap,
        tooltip: hasCompletedSwap ? 'route.cancelImpossible' : undefined,
      },
    ];
  }

  public openModal(
    uuid: string,
    vehicleUuid: string | null,
  ): OnyxModalResult<void> {
    return this.modalService.open<SwapModalData, void>(SwapModalComponent, {
      uuid,
      vehicleUuid,
    });
  }

  public share(uuid: string): void {
    ActionHelper.share(
      MapRoute.SWAP_CARD.replace(':uuid', uuid),
      this.toastService,
    );
  }

  public getShowOnMapOptions(
    swap: Swap,
    showOnMap: (vehicleUuid: string) => void,
  ): OnyxCallbackOption[] | null {
    return this.getShowOnOptions(swap, showOnMap);
  }

  public getShowOnGanttOptions(
    swap: Swap,
    showOnGantt: (vehicleUuid: string) => void,
  ): OnyxCallbackOption[] | null {
    return this.getShowOnOptions(swap, showOnGantt);
  }

  private getShowOnOptions(
    swap: Swap,
    callback: (vehicleUuid: string) => void,
  ): OnyxCallbackOption[] | null {
    if (!swap.otherVehicle) return null;

    const { primarySet, secondarySet } =
      swap.completedAt !== null
        ? {
            primarySet: swap.vehicleSet,
            secondarySet: swap.otherVehicleSet,
          }
        : {
            primarySet: swap.initialSet,
            secondarySet: swap.otherInitialSet,
          };

    return [
      {
        name: this.fleetIdentifierPipe.transform(
          [swap.vehicle, primarySet.trailer],
          'registrationNumber',
        ),
        value: () => callback(swap.vehicle.uuid),
        leftSquareColor: 'blue',
      },
      {
        name: this.fleetIdentifierPipe.transform(
          [swap.otherVehicle, secondarySet?.trailer ?? null],
          'registrationNumber',
        ),
        value: () => callback(swap.otherVehicle!.uuid),
        leftSquareColor: 'sea-green',
      },
    ];
  }
}
